import { Instance, applySnapshot, flow, getParent, getSnapshot, types } from 'mobx-state-tree';

import { getGrade, rejectGrade, saveGrade } from '@common/api/grades';
import { IMediaFile } from '@common/api/media';
import gradebookStatusId from '@common/constants/gradebookStatusId';
import Chunk from '@common/models/Chunks/Chunk';

import GradeType from '../GradeTypes/GradeType';
import Media from '../Media';
import { IGradeBookModel } from './GradeBook';

interface IVolatile {
	loading: boolean;
	annotatedFiles: Record<string, IMediaFile>;
}

const Grade = types
	.model('Grade', {
		id: types.identifierNumber,
		gradebook_id: types.number,
		chunk: types.maybeNull(Chunk),
		gradeType: types.maybeNull(GradeType),
		teacher_id: types.maybeNull(types.number),
		grade: types.maybeNull(types.string),
		manual_grade: types.maybeNull(types.string),
		comment: types.maybeNull(types.string),
		dictaphone_url: types.optional(types.array(types.string), []),
		dictaphone_file: types.optional(types.array(Media), []),
		student_url: types.optional(types.array(types.string), []),
		student_file: types.optional(types.array(Media), []),
		reject_comment: types.maybeNull(types.string),
		rejected_at: types.maybeNull(types.string),
		student_response: types.maybeNull(types.string),
		neutral_slide_key: types.maybeNull(types.string),
		grading_method: types.maybeNull(types.string),
		is_homework: types.maybe(types.maybeNull(types.boolean)),
		annotate_url: types.maybe(types.optional(types.array(types.string), [])),
		annotate_file: types.maybe(types.optional(types.array(Media), [])),
	})
	.volatile(
		(): IVolatile => ({
			loading: false,
			annotatedFiles: {},
		}),
	)
	.actions((self) => ({
		fetch: flow(function* () {
			try {
				self.loading = true;
				const result = yield getGrade(self.id);
				applySnapshot(self, result);
			} finally {
				self.loading = false;
			}
		}),
		setAnnotatedFiles(key: string, value: IMediaFile) {
			self.annotatedFiles[key] = value;
		},
	}))
	.actions((self) => ({
		save: flow(function* (data: any) {
			self.loading = true;
			try {
				const result = yield saveGrade(self.id, data);
				applySnapshot(self, result);
			} finally {
				self.loading = false;
			}
		}),
		reject: flow(function* (gradeId, data: any) {
			self.loading = true;
			try {
				const result = yield rejectGrade(gradeId, data);
				const parent = getParent<IGradeBookModel>(self, 2);
				const grades = [...getSnapshot(parent.grades)];
				grades[grades.length - 1] = result;

				applySnapshot(parent, {
					...parent,
					is_done: gradebookStatusId.reject,
					grades,
				});
			} finally {
				self.loading = false;
			}
		}),
	}));

export interface IGradeModel extends Instance<typeof Grade> {}

export default Grade;
